import {useState} from 'react';
import {Button, Popover} from '@ezcater/tapas';
import useTranslation from 'next-translate/useTranslation';

import useTracking from '@/hooks/useTracking';
import CorpAccountSelectionMenuButton from './CorpAccountSelectionMenuButton';
import {useCorpAccountSelectionModalContext} from './CorpAccountSelectionModalProvider';
import CorpAccountSelectionOption from './CorpAccountSelectionOption';
import useCorpAccountSelection from './useCorpAccountSelection';

export const VISIBLE_CORP_ACCOUNT_LIMIT = 8;

type CorpAccountDropdownProps = {
  defaultOpen?: boolean;
  onClose?: () => void;
};

const CorpAccountDropdown: React.FC<CorpAccountDropdownProps> = ({
  defaultOpen = false,
  onClose = () => {},
}) => {
  const {t} = useTranslation('app-bar');
  const {track} = useTracking();

  const {
    changeSelectedCorpAccountUuid,
    sortedCorpAccounts,
    currentCorpAccountUuid,
    selectedCorpAccount,
  } = useCorpAccountSelection();
  const {toggleCorpAccountSelectionModal} = useCorpAccountSelectionModalContext();
  const [isMenuOpen, setIsMenuOpen] = useState(defaultOpen);

  const handleMenuOpen = (value: boolean) => {
    if (value) {
      track('corp-account-switcher-opened', {
        page: window.location.pathname,
        url: window.location.href,
        misc_json: JSON.stringify({
          area: 'user-menu-desktop',
          account_count: sortedCorpAccounts.length,
          current_account_uuid: currentCorpAccountUuid,
        }),
      });
    }
    setIsMenuOpen(value);
  };

  const openCorpAccountSelectionModal = () => {
    toggleCorpAccountSelectionModal(true);
    setIsMenuOpen(false);
    onClose();

    track('corp-account-switcher-modal-viewed', {
      page: window.location.pathname,
      url: window.location.href,
      misc_json: JSON.stringify({
        total_accounts: sortedCorpAccounts.length,
        visible_accounts_limit: VISIBLE_CORP_ACCOUNT_LIMIT,
      }),
    });
  };

  const handleAccountClick = (accountUuid: string) => async () => {
    const previousAccountUuid = currentCorpAccountUuid;
    const previousAccount = sortedCorpAccounts.find(acc => acc.uuid === previousAccountUuid);
    const newAccount = sortedCorpAccounts.find(acc => acc.uuid === accountUuid);

    setIsMenuOpen(false);
    onClose();
    await changeSelectedCorpAccountUuid(accountUuid);

    track('corp-account-switched', {
      page: window.location.pathname,
      url: window.location.href,
      misc_json: JSON.stringify({
        area: 'user-menu-desktop',
        previous_account_uuid: previousAccountUuid,
        new_account_uuid: accountUuid,
        previous_account_name: previousAccount?.name || '',
        new_account_name: newAccount?.name || '',
        total_accounts: sortedCorpAccounts.length,
      }),
    });
  };

  if (sortedCorpAccounts.length <= 1) {
    return null;
  }

  return (
    <Popover.Root open={isMenuOpen} onOpenChange={handleMenuOpen}>
      <Popover.Trigger
        render={
          <CorpAccountSelectionMenuButton
            selectedCorpAccount={selectedCorpAccount}
            className="aria-expanded:underline"
          />
        }
      />
      <Popover.Content
        aria-label={t('menus.corporateAccounts.accountPicker.menuLabel')}
        side="left"
        alignOffset={-24}
        align="start"
        finalFocus={false}
      >
        <div
          role="listbox"
          aria-label={t('menus.corporateAccounts.accountPicker.listboxLabel')}
          className="mr-4 inline-flex min-w-80 max-w-96 flex-col items-start justify-start gap-2 rounded-lg border bg-white p-4 shadow-lg"
        >
          {sortedCorpAccounts.slice(0, VISIBLE_CORP_ACCOUNT_LIMIT).map(corpAccount => (
            <CorpAccountSelectionOption
              key={corpAccount.uuid}
              corpAccount={corpAccount}
              isSelected={currentCorpAccountUuid === corpAccount.uuid}
              onClick={handleAccountClick(corpAccount.uuid)}
              className="flex-1 self-stretch px-4 py-2"
            />
          ))}
          {sortedCorpAccounts.length > VISIBLE_CORP_ACCOUNT_LIMIT && (
            <Button onClick={openCorpAccountSelectionModal} variant="buttonLink">
              {t('menus.corporateAccounts.accountPicker.viewMoreLabel', {
                accountCount: sortedCorpAccounts.length,
              })}
            </Button>
          )}
        </div>
      </Popover.Content>
    </Popover.Root>
  );
};

export default CorpAccountDropdown;
